import {
    format,
    isSameHour,
    isSameMinute,
    isValid,
    isMatch,
    parse,
    set,
    startOfISOWeek,
    endOfISOWeek,
    endOfDay,
    startOfDay,
} from "date-fns";

export const SECOND_IN_MS = 1000;
export const MINUTE_IN_MS = 60 * SECOND_IN_MS;
export const HOUR_IN_MS = 60 * MINUTE_IN_MS;
export const DAY_IN_MS = 24 * HOUR_IN_MS;
export enum WeekDay {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

export interface TimeObject {
    hours: number;
    minutes: number;
    seconds: number;
}

// NB: (today_midnight - (today_midnight - 1s)) = 1d
export function dateDaysBetween(date1: Date, date2: Date): number {
    // copy date parts of the timestamps, discarding the time parts
    const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());
    // calculate the difference in milliseconds
    const difference_ms = d2.getTime() - d1.getTime();
    // convert back to days (round, not floor, because of daylight saving time)
    const days = Math.round(difference_ms / DAY_IN_MS);
    return days;
}

export function addDays(date: Date, amount: number): Date {
    const res = new Date(date.getTime() + amount * DAY_IN_MS);
    return res;
}

export function differenceInCalendarDays(dateLeft: Date, dateRight: Date): number {
    const startOfDayLeft = startOfDayTimestamp(dateLeft);
    const startOfDayRight = startOfDayTimestamp(dateRight);

    // Round the number of days to the nearest integer
    // because the number of milliseconds in a day is not constant
    // (e.g. it's different in the day of the daylight saving time clock shift)
    return Math.round((startOfDayLeft - startOfDayRight) / DAY_IN_MS);
}

/**
 * Normalizes a start/end date pair to the beginning of the start day (00:00:00.000)
 * and the end of the end day (23:59:59.999). If no end date is provided, the start
 * date is used for both bounds. Returns null if no start date is provided.
 */
export function getNormalizedDateRange(
    startDate: Date | null | undefined,
    endDate?: Date | null,
): { startDate: Date; endDate: Date } | null {
    if (!startDate) {
        return null;
    }
    return {
        startDate: startOfDay(startDate),
        endDate: endOfDay(endDate ?? startDate),
    };
}

export function startOfWeek(date: Date): Date {
    return startOfISOWeek(startOfDay(date));
}

export function endOfWeek(date: Date): Date {
    return endOfISOWeek(startOfDay(date));
}

export function isSameWeek(dateLeft: Date, dateRight: Date): boolean {
    const startOfWeekLeft = startOfWeek(dateLeft);
    const startOfWeekRight = startOfWeek(dateRight);
    return isSameTime(startOfWeekLeft, startOfWeekRight);
}

/**
 * Return if the date is in the current rolling year (from today to 12 months upfront)
 */
export function isDateInNextRollingYear(date: Date): boolean {
    const now = new Date();
    const oneYearAhead = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    return date >= now && date < oneYearAhead;
}

export function startOfDayTimestamp(date: Date): number {
    const originTime = date.getTime();
    const timeSinceMidnight = (originTime - date.getTimezoneOffset() * MINUTE_IN_MS) % DAY_IN_MS;
    const timestamp = originTime - timeSinceMidnight;
    return timestamp;
}

export function dateCompare(x: Date | null, y: Date | null): number {
    if (x == null) return y != null ? -1 : 0;
    if (y == null) return 1;
    return x.getTime() - y.getTime();
}

/**
 * Determines if time of date is midnight (00:00)
 * Midnight value is used as "empty time" in visit
 *
 * @param d
 */
export function isMidnight(d: Date): boolean {
    return !d.getHours() && !d.getMinutes();
}

/**
 * Set year, month and day from "from" date to "to" date
 * "to" date is modified and returned
 *
 * @param from
 * @param to
 */
export function setSameDay(from: Date | null, to: Date | null): Date | null {
    if (!from) {
        return to;
    }
    if (!to) {
        return null;
    }

    const newDate = set(to, {
        year: from.getFullYear(),
        month: from.getMonth(),
        date: from.getDate(),
    });
    return newDate;
}

/**
 * Set hour and minutes from "from" date to "to" date
 * "to" date is modified and returned
 *
 * @param from
 * @param to
 */
export function setSameTime(from: Date | null, to: Date | null): Date | null {
    if (!from) {
        return to;
    }
    if (!to) {
        return null;
    }

    const newDate = set(to, {
        hours: from.getHours(),
        minutes: from.getMinutes(),
        seconds: from.getSeconds(),
        milliseconds: 0,
    });
    return newDate;
}

/**
 * Returns true is hour and minute are equal on both dates
 *
 * @param date1
 * @param date2
 */
export function isSameTime(date1: Date, date2: Date): boolean {
    return isSameMinute(date1, date2) && isSameHour(date1, date2);
}

/**
 * Returns an HH:mm formatted string for date
 *
 * @param date
 */
export function getTimeFromDate(date: Date | null, withSeconds = false): string | null {
    if (!date) {
        return null;
    }

    const timeFormat = withSeconds ? "HH:mm:ss" : "HH:mm";
    return format(date, timeFormat);
}

/**
 * Parse input value as HH:mm or HH:mm:ss, uses rest of date data from "date" and returns a new date
 *
 * @param value
 * @param date
 */
export function parseTimeToDate(value: string, date: Date): Date | null {
    let newDate = parse(value, "HH:mm:ss", date);
    if (!isValidDate(newDate)) {
        newDate = parse(value, "HH:mm", date);

        if (!isValidDate(newDate)) {
            return null;
        }
    }

    return newDate;
}

/**
 * Returns true if date is valid
 */

export function isValidDate(date: Date): boolean {
    return isValid(date);
}

export function isValidTime(time: string | null): boolean {
    if (!time) {
        return false;
    }
    return isMatch(time, "HH:mm:ss") || isMatch(time, "HH:mm");
}

export interface DifferenceInWeekdaysOptions {
    /**
     * List of business days as numbers, e.g. [1, 2, 3, 4, 5] based on WeekDay enum.
     * If not provided, all days are considered business days.
     */
    businessDays?: number[];
    /**
     * List of holidays in "yyyy-MM-dd" format, e.g. ["2024-12-25", "2024-12-26"]
     */
    holidays?: string[];
    overrideWorkingDays?: string[];
}

export function addWorkingDays(date: Date, amount: number, context: DifferenceInWeekdaysOptions): Date {
    let result = new Date(date);
    let remaining = Math.abs(amount);
    const direction = Math.sign(amount);

    while (remaining > 0) {
        result = addDays(result, direction);
        if (isWorkingDay(result, context)) {
            remaining--;
        }
    }
    return result;
}

/**
 * @param businessDays list of business days as numbers, e.g. [1, 2, 3, 4, 5] based on WeekDay enum.
 */
export function isBusinessDay(date: Date, businessDays?: number[]) {
    if (businessDays == null) {
        return true;
    }
    return businessDays.includes(date?.getUTCDay());
}
/**
 * @param holidays list of holidays in "yyyy-MM-dd" format, e.g. ["2024-12-25", "2024-12-26"]
 */
export function isHoliday(date: Date, holidays: string[] | undefined): boolean {
    if (!holidays?.length) {
        return false;
    }
    return holidays.includes(format(date, "yyyy-MM-dd"));
}

function isWorkingDay(date: Date, options?: DifferenceInWeekdaysOptions) {
    if (options?.overrideWorkingDays?.length && isHoliday(date, options.overrideWorkingDays)) {
        return true;
    }
    return isBusinessDay(date, options?.businessDays) && !isHoliday(date, options?.holidays);
}

export function differenceInWorkingDays(
    laterDate: string | number | Date,
    earlierDate: string | number | Date,
    options?: DifferenceInWeekdaysOptions,
): number {
    const toLocalMidnight = (value: string | number | Date): Date => {
        const date = new Date(value);
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    };

    const start = toLocalMidnight(earlierDate);
    const end = toLocalMidnight(laterDate);

    const direction = start.getTime() <= end.getTime() ? 1 : -1;
    let count = 0;

    while (start.getTime() !== end.getTime()) {
        start.setDate(start.getDate() + direction);

        if (isWorkingDay(start, options)) {
            count += direction;
        }
    }

    return count;
}
